<template>
  <basic-card>
    <template slot="header">{{ $t('operation.listOf', {definitionName: operationDefinition.name}) }}</template>
    <toolbox>
      <toolbox-button :help-text="$t('operation.create')" @press="handleAdd">
        <icon :icon="['fal', 'plus']" size="lg"/>
      </toolbox-button>
      <toolbox-button :help-text="$t('operation.deleteSelected')"
                      :protected-message="$t('operation.deleteSelectedConfirmation', {count: selection.length})"
                      @press="deleteSelected">
        <icon :icon="['fal', 'trash']" size="lg"/>
      </toolbox-button>
      <toolbox-button :help-text="$t('operation.deleteQueried')"
                      :protected-message="$t('operation.deleteQueriedConfirmation')"
                      @press="deleteByQuery">
        <icon-layers class="fa-lg">
          <icon :icon="['fal', 'trash']"/>
          <icon :icon="['fas', 'circle']" transform="shrink-5 right-6 down-6"/>
          <icon :icon="['fas', 'fire-alt']" transform="shrink-8 right-6 down-6"
                style="color: var(--input-background-color);"/>
        </icon-layers>
      </toolbox-button>
      <!--      <toolbox-button help-text="Add cash-events from selected operations to payment batch" @press="paySelected">-->
      <!--        <icon :icon="['fal', 'cart-plus']" size="lg"/>-->
      <!--      </toolbox-button>-->
      <!--      <toolbox-button help-text="Add cash-events from queried operations to payment batch" @press="payByQuery">-->
      <!--        <icon-layers class="fa-lg">-->
      <!--          <icon :icon="['fal', 'cart-plus']"/>-->
      <!--          <icon :icon="['fas', 'circle']" transform="shrink-5 right-6 down-6"/>-->
      <!--          <icon :icon="['fas', 'fire-alt']" transform="shrink-8 right-6 down-6"-->
      <!--                style="color: var(&#45;&#45;input-background-color);"/>-->
      <!--        </icon-layers>-->
      <!--      </toolbox-button>-->
      <!--      <toolbox-button help-text="Batch update queried operations" @press="batchProcessQuery">-->
      <!--        <icon-layers class="fa-lg">-->
      <!--          <icon :icon="['fal', 'tools']"/>-->
      <!--          <icon :icon="['fas', 'circle']" transform="shrink-5 right-6 down-6"/>-->
      <!--          <icon :icon="['fas', 'fire-alt']" transform="shrink-8 right-6 down-6"-->
      <!--                style="color: var(&#45;&#45;input-background-color);"/>-->
      <!--        </icon-layers>-->
      <!--      </toolbox-button>-->
      <toolbox-button :help-text="$t('operation.exportCSV')" @press="csvExport">
        <icon :icon="['fal', 'file-csv']" size="lg"/>
      </toolbox-button>
    </toolbox>
    <framed-content :title="$t('searchQuery.queries')">
      <form-wrapper>
        <form-select-input :caption="$t('searchQuery.loadQuery')" v-model="operationQuery"
                           :options="operationQueryOptions"/>
        <form-text-input :caption="$t('searchQuery.queryName')" v-model="operationQueryName"/>
      </form-wrapper>

      <filter-form :filter-definition="filterDefinition" v-model="query" :key="formResetDate"/>
      <simple-horizontal-layout>
        <empty/>
        <empty/>
        <empty/>
        <simple-button :disabled="!query.id" confirmation-protected
                       :protection-message="` - ${$t('searchQuery.confirmDeleteQuery')}`"
                      @proceed="deleteFilter">
          {{ $t("searchQuery.deleteQuery") }}
        </simple-button>
        <simple-button @press="clearFilter">{{ $t("searchQuery.clearQuery") }}</simple-button>
        <simple-button @press="saveFilter">{{ $t("searchQuery.saveQuery") }}</simple-button>
        <simple-button @press="pageChanged">{{ $t("searchQuery.doQuery") }}</simple-button>
      </simple-horizontal-layout>
    </framed-content>

    <loader indefinite message="Loading operations" v-show="loading"/>
    <data-table
        forget-focused
        :key="operationDefinition.id"
        :data="operationsAdjusted"
        :pagination="pagination"
        :configuration="operationsTableConfig"
        @selectionChanged="handleSelectionChanged"
        @focusedChanged="handleFocusedChanged"
        @pageChanged="pageChanged"
        @sortChanged="sortChanged"
        @customAction="handleCustomAction"
        @add="handleAdd"
        @delete="handleDelete"
        @clone="handleClone"
        v-show="!loading"
        :component-id="`operations[${operationDefinition.id}]-list`"
    />
  </basic-card>
</template>

<script>
import BasicCard from "../components/layout/BasicCard";
import {mapGetters} from "vuex";
import DataTable from "../components/table/DataTable";
import SimpleButton from "../components/button/SimpleButton";
import FormWrapper from "../components/form/FormWrapper";
import FormTextInput from "../components/form/FormTextInput";
import CollapsibleContent from "../components/CollapsibleContent";
import SimpleHorizontalLayout from "../components/layout/SimpleHorizontalLayout";
import Empty from "../components/layout/Empty";
import FormSelectInput from "../components/form/FormSelectInput";
import FormNumberInput from "../components/form/FormNumberInput";
import QueryUtils from "../utils/QueryUtils"
import Toolbox from "../components/toolbox/Toolbox";
import ToolboxButton from "../components/toolbox/ToolboxButton";
import FilterForm from "../components/filters/FilterForm";
import Loader from "../components/Loader";
import FramedContent from "../components/layout/FramedContent";
import Axios from "axios";

const modifiedStyles = {'font-style': 'italic', 'background-color': 'rgba(200,83,83,0.15)'};

export default {
  name: "OperationsListCard",
  components: {
    FramedContent,
    Loader,
    FilterForm,
    ToolboxButton,
    Toolbox,
    FormNumberInput,
    FormSelectInput,
    Empty,
    SimpleHorizontalLayout,
    CollapsibleContent, FormTextInput, FormWrapper, SimpleButton, DataTable, BasicCard
  },
  props: {
    operationDefinition: Object,
    operations: Object,
    pagination: Object,
    loading: Boolean
  },
  data: function () {
    return {
      selection: [],
      formResetDate: new Date().getTime(),
      query: {
        parameters: {
          actualStatus: {type: 'enum', operator: 'in', value: 'LIVE', negation: false}
        }
      },
      cachedOperations: {},
      operationQueryName: "",
      operationQuery: null,
      operationsTableConfig: {
        enableCheck: true,
        enableAdd: true,
        allowEdit: true,
        columns: [
          {
            caption: this.$t('status'),
            datum: d => d.actualStatus,
            style: d => d.modified ? modifiedStyles : undefined
          },
          {
            caption: "Id",
            datum: d => d.id.substr(d.id.length - 5),
            style: d => d.modified ? modifiedStyles : undefined
          },
          {
            caption: "Zdroj",
            datum: d => d.source
          },
          {
            caption: this.$t('createdTimestamp'),
            datum: d => this.$formats.formatDateTime(d.createdTimestamp, false),
            style: d => d.modified ? modifiedStyles : undefined,
            sortField: `createdTimestamp`,
            allowSort: true
          }
        ],
        actions: [
          {
            name: "clone",
            icon: "clone",
            tooltip: this.$t("operation.cloneOperation")
          },
          {
            name: "delete",
            icon: "trash",
            tooltip: this.$t('operation.deleteOperation')
          }
        ]
      }
    }
  },
  watch: {
    operationQuery: {
      handler(newVal) {
        if(!newVal) return;
        let loadedQuery = this.getOperationQuery(newVal);
        this.query = loadedQuery;
        this.operationQueryName = loadedQuery.name;
        this.pageChanged();
      }
    },
    operationDefinition: {
      handler(newVal) {
        const defaultVal = par => d => d.content[par.name];

        const valueLambdas = {
          STRING: defaultVal,
          ENUM: par => d => {
            let value = d.content[par.name];
            let enumOption = par.details.enumValues.find(op => op.value == value);
            if (enumOption == undefined) return value;
            else return enumOption.name;
          },
          DATE: par => d => this.$formats.formatDate(defaultVal(par)(d), false),
          DECIMAL: par => d => this.$numeral(d.content[par.name] / Math.pow(10, par.details.precision)).format('0,0.' + '0'.repeat(par.details.precision)),
          INTEGER: par => d => this.$numeral(d.content[par.name]).format('0'),
          BOOLEAN: par => d => d.content[par.name] ? this.$t("yes") : this.$t("no"),
          GEO_POINT: par => d => {
            if (!d.content[par.name]) {
              return "N/A"
            }
            return `${this.$formats.formatAmount(d.content[par.name]?.longtitude)}/${this.$formats.formatAmount(d.content[par.name]?.latitude)}`;
          },
          ENTITY_REFERENCE: par => d => {
            let ref_id = d.content[par.name];
            if (par.details.entityType == 'account') {
              let acc = this.account(ref_id) ?? "N/A";
              return acc.name
            }
            if (par.details.entityType == 'party') {
              let party = this.party(ref_id) ?? "N/A";
              return party.displayName
            }
            if (par.details.entityType == 'operation') {
              return this.cachedOperations[ref_id] ?? "N/A"
            }
            return ref_id;
          },
          // CURRENCY: defaultVal,
          // ACCOUNT: par => d => {
          //     let acc = this.account(defaultVal(par)(d));
          //     if(acc == null) return "N/A";
          //     return acc.name
          // },
          // PARTY: par => d => {
          //     let party = this.party(defaultVal(par)(d));
          //     if(party == null) return "N/A";
          //     return party.displayName
          // },
          DYNA_MODEL: par => d => "Complex Object {...}",
          ARRAY: par => d => `${d.content[par.name].length} items`
        };

        const styleLambdas = {
          STRING: undefined,
          DATE: undefined,
          DECIMAL: d => ({'text-align': 'right'}),
          CURRENCY: undefined,
        }

        const operationCache = {};

        Object.keys(newVal.model.dynaProperties).map(key => newVal.model.dynaProperties[key]).forEach(par => {
          if (par.details.type === 'ENTITY_REFERENCE') {
            this.$store.dispatch("operations/fetchOperations", {
              requestedPage: 0,
              query: par.details.entityFilter,
              sort: {field: 'createdTimestamp', direction: -1},
              size: 100000
            })
                .then(data => {
                  data.list.forEach(op => operationCache[op.id] = op.name ?? op.id)
                })
          }

          this.operationsTableConfig.columns.push({
            caption: par.displayName,
            datum: valueLambdas[par.details.type](par),
            style: d => ({
              ...styleLambdas[par.details.type] ?? (d),
              ...d.modified ? modifiedStyles : undefined
            }),
            sortField: `content.values.${par.name}`,
            allowSort: true
          })
        })

        this.$set(this, 'cachedOperations', operationCache);


        let customActions = this.importDefinitions
            .filter(importDef => importDef.type == "EVENT_TRIGGER" && importDef.triggerActionTypes.includes('REQUESTED'))
            .map(importDef => {
              return {
                name: importDef.displayName,
                icon: importDef.icon,
                tooltip: importDef.description,
                handlerName: 'customAction',
                importDefinition: importDef
              }
            })

        this.operationsTableConfig.actions.push(...customActions);

        this.operationsTableConfig.objectName = this.operationDefinition.name;
      },
      immediate: true
    }
  },
  computed: {
    ...mapGetters({
      "operationQueries": "operationQueries/operationQueries",
      "getOperationQuery": "operationQueries/operationQuery",
      "currencies": "currencies/currencies",
      "currencyOptions": "currencies/currenciesOptions",
      "party": "parties/party",
      "parties": "parties/parties",
      "account": "banking/account",
      "accounts": "banking/accounts",
      "users": "users/users",
      "importDefinitions": "importDefinitions/importDefinitions"
    }),
    operationsAdjusted() {
      return [...this.operations.modifiedItems.map(op => ({...op, modified: true})),
        ...this.operations.actualItems.filter(item => !this.operations.modifiedItems.find(op => op.id == item.id)).map(op => ({
          ...op,
          modified: false
        }))]
    },
    operationQueryOptions() {
      return this.operationQueries
          .filter(query => query.parameters.operationDefinition.value == this.operationDefinition.id)
          .map(query => ({id: query.name, name: query.name == null ? "No-name" : query.name}))
    },
    accountOptions() {
      return this.accounts.map(acc => ({id: acc.id, name: acc.name}))
    },
    partyOptions() {
      return this.parties.map(pty => ({id: pty.id, name: pty.displayName == null ? "N/A" : pty.displayName}))
    },
    userOptions() {
      return this.users.map(usr => ({id: usr.id, name: usr.username}))
    },
    filterDefinition() {
      let filterDefinition = QueryUtils.OperationQueryDefinition(this.operationDefinition, {
        accounts: this.accountOptions,
        currencies: this.currencyOptions,
        parties: this.partyOptions,
        users: this.userOptions
      });

      console.log('filter', filterDefinition)
      console.log('filter', this.query)

      return filterDefinition;
    },
    transformedQuery() {
      let resultObject = this.$_.cloneDeep(this.query)
      resultObject.parameters.operationDefinition = {
        operator: "eq",
        value: this.operationDefinition.id,
        type: "string"
      };
      return resultObject;
    }
  },
  methods: {
    sortChanged({sortField, direction}) {
      const sorting = sortField === null ? undefined : {sortField, direction};
      this.$emit('focusedChanged', null);
      this.$emit("sortChanged", sorting, this.transformedQuery);
    },
    pageChanged(page, pageSize) {
      this.$emit('focusedChanged', null);
      this.$emit('pageChanged', page, this.transformedQuery, pageSize)
    },
    handleCustomAction(focused, action) {
      this.handleExecute(action.importDefinition, focused);
    },
    handleFocusedChanged(focused) {
      this.$emit('focusedChanged', focused);
    },
    handleDelete(toDelete) {
      this.$emit('delete', toDelete, this.transformedQuery)
    },
    handleAdd() {
      this.$emit('add');
    },
    handleClone(clone) {
      this.$emit('clone', clone);
    },
    saveFilter() {
      let toSave = this.$_.cloneDeep(this.transformedQuery);
      if (this.operationQueryName == "" ||
          this.operationQueries.map(q => q.name).includes(this.operationQueryName) && !this.query?.id) {
        this.$notify({title: "Filtr musí mít při uložení zadané unikátní název", group: "application", type: "error"})
        return;
      }
      toSave.name = this.operationQueryName;
      toSave.object = "operation"
      this.$store.dispatch("operationQueries/saveOperationQuery", toSave)
          .then(() => {
            this.$notify({
              group: 'application',
              title: `Query ${toSave.name} saved`,
              text: `Query ${this.operationQueryName} was successfully saved`,
              type: "ok"
            });
          });
    },
    csvExport() {
      this.$emit('csvRequested', this.transformedQuery);
    },
    deleteByQuery() {
      this.$emit('focusedChanged', null);
      this.$emit("deleteByQueryRequested", this.transformedQuery)
    },
    deleteSelected() {
      this.$emit('focusedChanged', null);
      this.$emit("deleteSelectedRequested", this.selection, this.transformedQuery);
    },
    handleExecute(def, operation) {
      return Axios.post("processing/api/v1/processes", {
        values: {payload: operation, action: "REQUESTED"},
        definitionId: def.id,
      }).then(res => {
        if (res.status === 201) {
          this.$notify({
            group: 'application',
            title: `Execution of script started`,
            text: `Execution of ${def.displayName} started`,
            type: "ok"
          })
        } else if (res.status === 409) {
          this.$notify({
            group: 'application',
            title: 'Execution failed',
            text: `Execution of ${def.displayName} failed`,
            type: "error"
          })
        }
      });
    },
    batchProcessQuery() {

    },
    payByQuery() {
      this.$emit("payByQueryRequested", this.transformedQuery)
    },
    paySelected() {
      this.$emit('paySelectedRequested', this.selection, this.transformedQuery);
    },
    handleSelectionChanged(selection) {
      this.selection = selection;
    },
    clearFilter() {
      this.operationQuery = '';
      this.operationQueryName = '';
      this.query = {parameters: {actualStatus: {type: 'enum', operator: 'in', value: 'LIVE', negation: false}}};
      this.formResetDate = new Date().getTime();
      this.pageChanged();
    },
    async deleteFilter() {
      if (this.query.id) {
        await this.$store.dispatch("operationQueries/deleteOperationQuery", this.query);
        await this.clearFilter();
      }
    },
  }
}
</script>

<style lang="scss" scoped>

</style>
